<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppBroadcastListsAPI from 'dashboard/api/whatsappBroadcastLists';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
});

const modelValue = defineModel({
  type: Number,
  default: null,
});

const { t } = useI18n();

const isLoading = ref(false);
const lists = ref([]);

const createName = ref('');
const createDescription = ref('');
const createFile = ref(null);
const fileInputRef = ref(null);

const options = computed(() =>
  (lists.value || []).map(l => ({
    value: l.id,
    label: l.name,
  }))
);

const exampleCsv = computed(
  () => `name,phone\nJohn Doe,+5531999999999\n, +5531888888888\n`
);

const loadLists = async () => {
  if (!props.inboxId) return;
  isLoading.value = true;
  try {
    const response = await WhatsAppBroadcastListsAPI.list({
      inboxId: props.inboxId,
    });
    lists.value = response?.data?.payload || [];
  } catch (e) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const onPickFile = e => {
  const file = e?.target?.files?.[0] || null;
  createFile.value = file;
};

const createList = async () => {
  if (!createName.value?.trim()) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.NAME_REQUIRED'));
    return;
  }
  if (!createFile.value) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.FILE_REQUIRED'));
    return;
  }
  isLoading.value = true;
  try {
    const response = await WhatsAppBroadcastListsAPI.create({
      inboxId: props.inboxId,
      name: createName.value.trim(),
      description: createDescription.value?.trim() || '',
      file: createFile.value,
    });
    const created = response?.data?.payload;
    if (created?.id) {
      modelValue.value = created.id;
      await loadLists();
      useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.CREATED'));
      createName.value = '';
      createDescription.value = '';
      createFile.value = null;
      if (fileInputRef.value) fileInputRef.value.value = '';
    }
  } catch (e) {
    useAlert(t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.CREATE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadLists);

watch(
  () => props.inboxId,
  () => {
    modelValue.value = null;
    loadLists();
  }
);
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.LABEL') }}
      </label>
      <ComboBox
        v-model="modelValue"
        :options="options"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.PLACEHOLDER')
        "
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
      <p class="mt-1 text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.INFO') }}
      </p>
    </div>

    <div
      class="rounded-xl border border-n-weak bg-n-alpha-black2 p-4 flex flex-col gap-3"
    >
      <div class="text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.IMPORT_TITLE') }}
      </div>

      <Input
        v-model="createName"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.NAME_LABEL')"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.NAME_PLACEHOLDER')
        "
      />
      <Input
        v-model="createDescription"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.DESC_LABEL')"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.DESC_PLACEHOLDER')
        "
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.FILE_LABEL') }}
        </label>
        <input
          ref="fileInputRef"
          type="file"
          accept=".csv,text/csv"
          class="text-sm text-n-slate-12"
          @change="onPickFile"
        />
      </div>

      <div class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.CSV_FORMAT_TITLE') }}
        <pre
          class="mt-2 whitespace-pre-wrap rounded-lg bg-n-alpha-2 p-3 text-xs text-n-slate-12"
          >{{ exampleCsv }}</pre
        >
      </div>

      <div class="flex justify-end">
        <Button
          size="sm"
          :label="
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.BROADCAST_LIST.IMPORT_BUTTON')
          "
          :is-loading="isLoading"
          :disabled="isLoading"
          @click="createList"
        />
      </div>
    </div>
  </div>
</template>
